import React from 'react'
import { Container } from '@/src/ct-design/lib'
import Modal from '@/src/ct-design/lib/Modal'
import ModalTitle from '@/src/ct-design/lib/Modal/ModalTitle/ModalTitle'
import ModalContent from '@/src/ct-design/lib/Modal/ModalContent/ModalContent'
import { DeviceType } from '@/src/types'

interface TermsAndConditionsModalProps {
    isOpen: boolean
    onClose: () => void
    serviceRule: string | undefined
    deviceType: DeviceType
}

// Helper to prettify the HTML string for terms
function renderTermsBlocks(html: string, isDesktop: boolean) {
    const decodeEscapedHtml = (value: string) => {
        const textarea = document.createElement('textarea')
        textarea.innerHTML = value
        return textarea.value
    }

    const normalizedHtml = decodeEscapedHtml(html)
    const parser = new DOMParser()
    const doc = parser.parseFromString(`<div>${normalizedHtml}</div>`, 'text/html')
    // Handle <ol><li>...</li></ol> format
    const ol = doc.querySelector('ol')
    if (ol) {
        const items = Array.from(ol.querySelectorAll('li'))
        return (
            <div>
                {items.map((li, idx) => (
                    <div
                        key={idx}
                        style={{
                            marginBottom: isDesktop ? 18 : 12,
                            padding: isDesktop ? '16px 14px' : '10px 6px',
                            background:
                                idx % 2 === 0
                                    ? 'rgba(245,247,250,0.7)'
                                    : 'transparent',
                            borderRadius: 8,
                            border: '1px solid #f0f1f3',
                            display: 'flex',
                            alignItems: 'flex-start',
                            fontSize: isDesktop ? 17 : 15.5,
                            color: '#23272F',
                            fontWeight: 400,
                            lineHeight: isDesktop ? 1.8 : 1.7,
                            letterSpacing: '0.01em'
                        }}
                    >
                        <span
                            style={{
                                fontWeight: 600,
                                marginRight: 8,
                                minWidth: 28,
                                display: 'inline-block',
                                color: '#1a1a1a',
                                fontSize: isDesktop ? 17 : 15.5
                            }}
                        >
                            {idx + 1}.
                        </span>
                        <span style={{ whiteSpace: 'pre-line' }}>
                            {li.textContent?.trim()}
                        </span>
                    </div>
                ))}
            </div>
        )
    }
    // Fallback: handle <p>...</p> format
    const paragraphs = Array.from(doc.querySelectorAll('p'))
    if (paragraphs.length === 0) return html
    return (
        <div>
            {paragraphs.map((p, idx) => {
                let text = p.textContent || ''
                const match = text.match(/^(\d+)([\.)]?)(\s+)/)
                let number = ''
                let rest = text
                if (match) {
                    number = match[1] + (match[2] === '.' ? '.' : '.')
                    rest = text.slice(match[0].length)
                }
                return (
                    <div
                        key={idx}
                        style={{
                            marginBottom: isDesktop ? 18 : 12,
                            padding: isDesktop ? '16px 14px' : '10px 6px',
                            background:
                                idx % 2 === 0
                                    ? 'rgba(245,247,250,0.7)'
                                    : 'transparent',
                            borderRadius: 8,
                            border: '1px solid #f0f1f3',
                            display: 'flex',
                            alignItems: 'flex-start',
                            fontSize: isDesktop ? 17 : 15.5,
                            color: '#23272F',
                            fontWeight: 400,
                            lineHeight: isDesktop ? 1.8 : 1.7,
                            letterSpacing: '0.01em'
                        }}
                    >
                        {number && (
                            <span
                                style={{
                                    fontWeight: 600,
                                    marginRight: 8,
                                    minWidth: 28,
                                    display: 'inline-block',
                                    color: '#1a1a1a',
                                    fontSize: isDesktop ? 17 : 15.5
                                }}
                            >
                                {number}
                            </span>
                        )}
                        <span style={{ whiteSpace: 'pre-line' }}>
                            {rest.trim()}
                        </span>
                    </div>
                )
            })}
        </div>
    )
}

const TermsAndConditionsModal: React.FC<TermsAndConditionsModalProps> = ({
    isOpen,
    onClose,
    serviceRule,
    deviceType
}) => {
    const isDesktop = deviceType === 'DESKTOP'
    return (
        <Modal
            open={isOpen}
            onClose={onClose}
            size={isDesktop ? 'LARGE' : 'SMALL'}
        >
            <Container
                style={{
                    background: '#fff',
                    borderRadius: 20,
                    boxShadow: '0 8px 40px rgba(0,0,0,0.16)',
                    padding: isDesktop
                        ? '44px 56px 36px 56px'
                        : '28px 10px 22px 10px',
                    minWidth: isDesktop ? 600 : 'auto',
                    width: '100%',
                    boxSizing: 'border-box',
                    border: '1.5px solid #e5e7eb',
                    maxHeight: isDesktop ? 640 : 440,
                    overflow: 'hidden',
                    position: 'relative'
                }}
            >
                <div
                    style={{
                        borderBottom: '1.5px solid #f0f1f3',
                        marginBottom: isDesktop ? 28 : 18,
                        paddingBottom: isDesktop ? 18 : 10,
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}
                >
                    <ModalTitle>Terms & Conditions</ModalTitle>
                </div>
                <ModalContent>
                    <div
                        style={{
                            maxHeight: isDesktop ? 500 : 300,
                            overflowY: 'auto',
                            padding: isDesktop ? '0 4px' : '0 2px',
                            fontSize: isDesktop ? 18 : 15.5,
                            color: '#23272F',
                            lineHeight: isDesktop ? 1.85 : 1.7,
                            letterSpacing: '0.01em',
                            fontWeight: 400,
                            background: 'transparent',
                            borderRadius: 12,
                            marginTop: isDesktop ? 8 : 4,
                            marginBottom: isDesktop ? 8 : 4,
                            border: 'none'
                        }}
                    >
                        {serviceRule
                            ? renderTermsBlocks(serviceRule, isDesktop)
                            : null}
                    </div>
                </ModalContent>
            </Container>
        </Modal>
    )
}

export default TermsAndConditionsModal
